import { Color, Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon, ArrowRightIcon } from '@persona-id/design-tokens/icons';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import FormContactUsV2 from 'dashboard/forms/FormContactUsV2';
import type { FormContactUsV2Data } from 'dashboard/forms/FormContactUsV2/FormContactUsV2';
import { Container, Header } from 'dashboard/layouts/IndexViewLayoutV2';
import { User } from 'dashboard/models/user';
import {
  helpCategories,
  HelpCategory,
  HelpCategoryName,
  popularLinks,
} from 'dashboard/views/DashboardContactUsView/constants';
import DashboardContactUsSearch from 'dashboard/views/DashboardContactUsView/DashboardContactUsSearch';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';

interface DashboardContactUsViewProps {
  currentUser: User | null; // can be null if the user is not logged in
  createTicket: (data: FormContactUsV2Data) => void;
}

const DashboardContactUsView = ({ currentUser, createTicket }: DashboardContactUsViewProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');

    if (category) {
      const foundCategory = helpCategories.find((c) => c.name === category);
      if (foundCategory) {
        setSelectedCategory(foundCategory);
      }
    }
  }, [location.search]);

  const handleUpdateSelectedCategory = (category: HelpCategory | null) => {
    setSelectedCategory(category);

    if (category) {
      navigate(`?category=${category.name}`);
    } else {
      const params = new URLSearchParams(location.search);
      params.delete('category');
      navigate({ search: params.toString() });
    }
  };

  return (
    <Container>
      <Header title='Get support' />
      <CardWrapper>
        <Card.Body>
          <HowCanWeHelpWrapper>
            <Heading color='primary' size='lg'>
              How can we help?
            </Heading>
            <Spacer height={2} />
            <DashboardContactUsSearch />
          </HowCanWeHelpWrapper>
          <TwoColumnWrapper>
            <PopularLinksWrapper>
              <Heading color='primary' size='md'>
                Popular links
              </Heading>
              {popularLinks.map((link) => (
                <a
                  href={link.link}
                  target='_blank'
                  rel='noopener noreferrer'
                  key={`popular-link-${link.name}`}
                >
                  <PopularLink>
                    <PopularLinkIcon>{link.icon}</PopularLinkIcon>
                    <div>{link.title}</div>
                  </PopularLink>
                </a>
              ))}
            </PopularLinksWrapper>
            <SubmitRequestCard>
              <Card.Body>
                <Heading color='primary' size='md'>
                  I need more help with...
                </Heading>
                {selectedCategory ? (
                  <HelpCategorySelectedWrapper>
                    <HelpCategorySelectedHeader>
                      <Heading size='sm'>{selectedCategory.title}</Heading>
                      <Button
                        variant='basic'
                        size='sm'
                        width='auto'
                        prefixIcon={<ArrowLeftIcon />}
                        onClick={() => handleUpdateSelectedCategory(null)}
                      >
                        Go back
                      </Button>
                    </HelpCategorySelectedHeader>
                    {selectedCategory.hasCustomContactFormFlow ? (
                      <>
                        {selectedCategory.name === HelpCategoryName.EndUser && (
                          <EndUserHelp currentUser={currentUser} createTicket={createTicket} />
                        )}
                        {selectedCategory.name === HelpCategoryName.Sales && (
                          <SalesHelp currentUser={currentUser} createTicket={createTicket} />
                        )}
                        {selectedCategory.name === HelpCategoryName.ReportIssue && (
                          <ReportIssueHelp />
                        )}
                        {selectedCategory.name === HelpCategoryName.ProductionAccess && (
                          <ProductionAccessHelp
                            currentUser={currentUser}
                            createTicket={createTicket}
                          />
                        )}
                      </>
                    ) : (
                      <FormWrapper>
                        <FormContactUsV2
                          currentUser={currentUser}
                          onSubmit={createTicket}
                          helpCategoryName={selectedCategory.name}
                        />
                      </FormWrapper>
                    )}
                  </HelpCategorySelectedWrapper>
                ) : (
                  <>
                    {helpCategories.map((category) => {
                      if (currentUser || (!currentUser && !category.requiresLoggedIn)) {
                        return (
                          <HelpCategoryButton
                            key={`contact-us__help-category-${category.name}`}
                            onClick={() => handleUpdateSelectedCategory(category)}
                          >
                            {category.title}
                            <ArrowRightIcon />
                          </HelpCategoryButton>
                        );
                      }
                      return null;
                    })}
                    {!currentUser && (
                      <>
                        <Spacer height={1} />
                        <Paragraph color='primary'>
                          Please <Link to='/dashboard/login'>login</Link> if you would like to
                          contact us with other types of questions.
                        </Paragraph>
                      </>
                    )}
                  </>
                )}
              </Card.Body>
            </SubmitRequestCard>
          </TwoColumnWrapper>
        </Card.Body>
      </CardWrapper>
    </Container>
  );
};

const EndUserHelp = ({
  currentUser,
  createTicket,
}: {
  currentUser: User | null;
  createTicket: (data: FormContactUsV2Data) => void;
}) => {
  const [showContactForm, setShowContactForm] = useState(false);

  return (
    <Card>
      <Card.Body>
        <EndUserHelpLinkedIn>
          <Paragraph color='primary' size='md'>
            <b>For LinkedIn users:</b>
          </Paragraph>
          <Spacer height={1} />
          <Paragraph color='primary' size='md'>
            To give you an easier way to build authenticity with your professional community,
            LinkedIn has partnered with Persona to allow individuals to securely and easily verify
            their identity. Use these links to answer frequently asked questions about:
          </Paragraph>
          <UnorderedList>
            <li>
              <a
                rel='noopener noreferrer'
                target='_blank'
                href='https://help.withpersona.com/end-users/linkedin/get-verified/'
              >
                Gettting verified on LinkedIn with Persona
              </a>
            </li>
            <li>
              <a
                rel='noopener noreferrer'
                target='_blank'
                href='https://help.withpersona.com/end-users/linkedin/account-recovery/'
              >
                Recovering access to your LinkedIn account using Government-Issued ID
              </a>
            </li>
          </UnorderedList>
          <Paragraph color='primary' size='md'>
            For further questions, please{' '}
            <a
              href='https://www.linkedin.com/help/linkedin/solve'
              rel='noopener noreferrer'
              target='_blank'
            >
              contact LinkedIn support
            </a>
            .
          </Paragraph>
        </EndUserHelpLinkedIn>
        <Spacer height={1} />
        <Paragraph color='primary' size='md'>
          Many businesses choose to partner with Persona to verify the identities of their
          customers, and each business has their own unique requirements for verification. If you’re
          having trouble completing your Persona identity verification, use{' '}
          <a
            href='https://help.withpersona.com/for-end-users/'
            rel='noopener noreferrer'
            target='_blank'
          >
            these resources
          </a>{' '}
          to troubleshoot. You’ll find suggestions on FAQs, such as:
        </Paragraph>
        <UnorderedList>
          <li>Handling expired verification links</li>
          <li>Scanning passports</li>
          <li>Continuing on a different device</li>
        </UnorderedList>
        <Paragraph color='primary' size='md'>
          If you’re still having trouble,{' '}
          <b>we recommend contacting the organization that directed you to the Persona flow</b>.
          They will be able to better assist you with your verification.
        </Paragraph>
        <Spacer height={2} />
        {showContactForm ? (
          <FormContactUsV2
            currentUser={currentUser}
            helpCategoryName={HelpCategoryName.EndUser}
            onSubmit={createTicket}
          />
        ) : (
          <Button size='sm' variant='basic' width='button' onClick={() => setShowContactForm(true)}>
            I’m still having trouble
          </Button>
        )}
      </Card.Body>
    </Card>
  );
};

const SalesHelp = ({
  currentUser,
  createTicket,
}: {
  currentUser: User | null;
  createTicket: (data: FormContactUsV2Data) => void;
}) => {
  return (
    <Card>
      <Card.Body>
        <Paragraph color='primary' size='md'>
          You can find more information about our plans and pricing{' '}
          <a href='https://withpersona.com/pricing' rel='noopener noreferrer' target='_blank'>
            here
          </a>
          .
        </Paragraph>
        <Spacer height={1} />
        <hr />
        <Spacer height={1} />
        <FormContactUsV2
          currentUser={currentUser}
          helpCategoryName={HelpCategoryName.Sales}
          onSubmit={createTicket}
        />
      </Card.Body>
    </Card>
  );
};

const ReportIssueHelp = () => {
  return (
    <Card>
      <Card.Body>
        <Paragraph color='primary' size='md'>
          If you’re having an issue with a specific Inquiry or Verification (e.g. text extraction
          issue, database matching issue, or suspected fraud), the best way to get help is to report
          the issue directly from your Persona Dashboard. This will automatically include the
          information we need to investigate the issue.
        </Paragraph>
        <Paragraph color='primary' size='md'>
          On the page where you are viewing the Inquiry or the Verification, click the{' '}
          <b>Report issue</b> button in the top right corner. This will open a form where you can
          provide more information. For more information, visit{' '}
          <a
            href='https://help.withpersona.com/articles/4hBzcoBM9lVCMZv6PFw31V/'
            rel='noopener noreferrer'
            target='_blank'
          >
            this article
          </a>
          .
        </Paragraph>
      </Card.Body>
    </Card>
  );
};

const ProductionAccessHelp = ({
  currentUser,
  createTicket,
}: {
  currentUser: User | null;
  createTicket: (data: FormContactUsV2Data) => void;
}) => {
  const [showContactForm, setShowContactForm] = useState(false);

  return (
    <Card>
      <Card.Body>
        <Paragraph color='primary' size='md'>
          Thanks for considering Persona. If you’ve recently applied for the{' '}
          <a href='https://withpersona.com/startups' rel='noopener noreferrer' target='_blank'>
            Persona Startup Program
          </a>{' '}
          or the Essential plan and are in the process of verifying your business, please note that
          the business verification team is actively reviewing cases and may have reached out to
          obtain additional information. Due to a recent high volume interest in our programs and
          plans, you can expect the review process to take 3-5 business days.
        </Paragraph>
        <Spacer height={2} />
        {showContactForm ? (
          <FormContactUsV2
            currentUser={currentUser}
            helpCategoryName={HelpCategoryName.ProductionAccess}
            onSubmit={createTicket}
          />
        ) : (
          <Button size='sm' variant='basic' width='button' onClick={() => setShowContactForm(true)}>
            I’m still having trouble
          </Button>
        )}
      </Card.Body>
    </Card>
  );
};

const CardWrapper = styled(Card)`
  width: 90%;
  margin: ${Spacing[6]};

  hr {
    border-top: 1px solid ${Color.BorderDivider};
  }
`;

const HowCanWeHelpWrapper = styled.div`
  text-align: center;
  width: 80%;
  max-width: 700px;
  margin: ${Spacing[4]} auto ${Spacing[8]} auto;

  @media (max-width: 1120px) {
    width: 100%;
  }
`;

const TwoColumnWrapper = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[6]};
  padding-top: ${Spacing[6]};
  border-top: 1px solid ${Color.BorderDivider};

  @media (max-width: 1120px) {
    flex-direction: column;
  }
`;

const PopularLinksWrapper = styled.div`
  margin-top: ${Spacing[5]};
  min-width: 300px;

  @media (max-width: 1120px) {
    order: 2;
    min-width: auto;
  }
`;

const PopularLink = styled.div`
  max-width: 300px;
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[3]};
  margin: ${Spacing[4]} 0px;
  padding: ${Spacing[2]} ${Spacing[4]};
  cursor: pointer;
  color: ${Color.TextPrimary};
  border: 1px solid ${Color.BorderDivider};
  border-radius: ${Spacing[1]};
  transition: ${TransitionDuration.Medium};

  &:hover {
    scale: 1.01;
  }

  @media (max-width: 1120px) {
    max-width: 100%;
  }
`;

const PopularLinkIcon = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`;

const SubmitRequestCard = styled(Card)`
  border: 1px solid ${Color.BorderDivider};
  border-radius: ${Spacing[1]};
  flex-grow: 1;
`;

const HelpCategoryButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};
  text-align: left;
  margin: ${Spacing[2]} 0px;
  padding: ${Spacing[4]};
  cursor: pointer;
  color: ${Color.TextPrimary};
  background-color: ${Color.PrimaryWhite};
  border: 1px solid ${Color.BorderDivider};
  border-radius: ${Spacing[1]};
  transition: ${TransitionDuration.Medium};

  &:hover {
    scale: 1.01;
  }
`;

const HelpCategorySelectedWrapper = styled.div`
  padding: ${Spacing[4]};
  margin-top: ${Spacing[2]};
  border: 1px solid ${Color.BorderDivider};
  border-radius: ${Spacing[1]};
`;

const HelpCategorySelectedHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: ${Spacing[2]};
  margin: ${Spacing[2]} ${Spacing[4]};
`;

const FormWrapper = styled.div`
  margin: ${Spacing[4]};
`;

const UnorderedList = styled.ul`
  margin: ${Spacing[2]} 0px ${Spacing[2]} ${Spacing[4]};
`;

const EndUserHelpLinkedIn = styled.div`
  background-color: ${Color.CoreBlue100};
  padding: ${Spacing[4]};
  border-radius: ${Spacing[2]};
`;

export default DashboardContactUsView;
